import { useEffect, useRef } from "react";

/** How often both faces refresh the open thread and the unread count (until the live hub, US-56). */
export const MESSAGES_POLL_MS = 30_000;

const isVisible = (): boolean => typeof document === "undefined" || document.visibilityState === "visible";

/**
 * Runs `callback` now and then every `intervalMs` while `enabled` and the tab is visible,
 * restarting when `resetKey` changes (e.g. the guardian switches member). A hidden tab neither
 * polls nor marks anything read; coming back to it runs `callback` at once. The latest callback is
 * always used, so callers need not memoize it.
 */
export const usePolling = (
  callback: () => void,
  enabled: boolean,
  resetKey = "",
  intervalMs = MESSAGES_POLL_MS,
): void => {
  const latest = useRef(callback);
  latest.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => {
      if (isVisible()) latest.current();
    };
    tick();
    const handle = window.setInterval(tick, intervalMs);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(handle);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [enabled, resetKey, intervalMs]);
};
